import { useMemo } from 'react';

import {
  KentRebalancingPlanStatusEnum,
  NormalizedRebalancingPlan,
  RebalancingPlanStatusEnum,
} from '@cast/types';
import { notReachable } from '@cast/utils';

import {
  useKentRebalancePlansQuery,
  useRebalancePlansQuery,
} from 'hooks/queries/rebalancer';
import {
  KENT_REBALANCING_IN_PROGRESS_STATES,
  REBALANCING_IN_PROGRESS_STATES,
} from 'constants/cluster';

import { useIsKentRebalancingMode } from './useIsKentRebalancingMode';
import {
  mapAutoscalerRebalancingPlanToNormalizedRebalancingPlan,
  mapKentRebalancingPlanToNormalizedRebalancingPlan,
} from '../utils/normalizedRebalancingPlan';

/**
 * Semantic status filter for the "correct" rebalance plans query.
 * Introduced because autoscaler and Kent have different status enums with
 * no 1:1 mapping. Using a semantic filter lets callers express
 * intent without referencing a specific API's status enum, and each variant
 * resolves it independently to its own enum values.
 */
type RebalancingPlanStatusFilter = 'in_progress' | 'generating';

type UseCorrectRebalancePlansQueryProps = {
  clusterId?: string;
  disabled?: boolean;
  limit?: number;
  statusFilter?: RebalancingPlanStatusFilter;
  /** Autoscaler only — Kent always returns these */
  includeOperations?: boolean;
  /** Autoscaler only — Kent always returns these */
  includeConfigurations?: boolean;
  /** Autoscaler only — Kent always returns these */
  includeNegative?: boolean;
};

type UseCorrectRebalancePlansQuery = {
  isLoading: boolean;
  normalizedRebalancingPlans?: NormalizedRebalancingPlan[];
  error?: unknown;
  refetch?: () => void;
  fetchNextPage?: () => void;
  isFetching?: boolean;
  hasNextPage?: boolean;
};

const resolveAutoscalerStatuses = (
  filter: RebalancingPlanStatusFilter | undefined
): RebalancingPlanStatusEnum[] | undefined => {
  if (!filter) return undefined;
  switch (filter) {
    case 'in_progress':
      return REBALANCING_IN_PROGRESS_STATES;
    case 'generating':
      return [RebalancingPlanStatusEnum.generating];
    default:
      return notReachable(filter);
  }
};

const resolveKentStatuses = (
  filter: RebalancingPlanStatusFilter | undefined
): KentRebalancingPlanStatusEnum[] | undefined => {
  if (!filter) return undefined;
  switch (filter) {
    case 'in_progress':
      return KENT_REBALANCING_IN_PROGRESS_STATES;
    case 'generating':
      return [KentRebalancingPlanStatusEnum.GENERATING];
    default:
      return notReachable(filter);
  }
};

export const useCorrectRebalancePlansQuery = (
  params: UseCorrectRebalancePlansQueryProps
): UseCorrectRebalancePlansQuery => {
  const shouldUseKentApi = useIsKentRebalancingMode();

  const {
    clusterId,
    disabled,
    limit,
    statusFilter,
    includeOperations,
    includeConfigurations,
    includeNegative,
  } = params;

  const regularResponse = useRebalancePlansQuery({
    clusterId,
    disabled: disabled || shouldUseKentApi,
    limit,
    includeStatuses: resolveAutoscalerStatuses(statusFilter),
    includeOperations,
    includeConfigurations,
    includeNegative,
  });

  const kentResponse = useKentRebalancePlansQuery({
    clusterId,
    disabled: disabled || !shouldUseKentApi,
    limit,
    includeStatuses: resolveKentStatuses(statusFilter),
  });

  const normalizedRebalancingPlans = useMemo<
    NormalizedRebalancingPlan[] | undefined
  >(() => {
    if (shouldUseKentApi) {
      return kentResponse.rebalancePlans?.map(
        mapKentRebalancingPlanToNormalizedRebalancingPlan
      );
    }

    return regularResponse.rebalancePlans?.map(
      mapAutoscalerRebalancingPlanToNormalizedRebalancingPlan
    );
  }, [
    shouldUseKentApi,
    kentResponse.rebalancePlans,
    regularResponse.rebalancePlans,
  ]);

  return shouldUseKentApi
    ? { ...kentResponse, normalizedRebalancingPlans }
    : { ...regularResponse, normalizedRebalancingPlans };
};
